import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    initializeBankIDDetails,
    initializeBankIDFlow,
    initializeBankIdVerificationDetails,
    initializeBankIdVerificationFlow,
    checkBankIDAuthStatus,
    checkBankIdVerificationAuthStatus,
    authenticateWithBankIDSameDevice,
    setQRModalOpen,
    setNeedsReload,
    resetBankIDFlow,
    clearQRData,
    resetBankIDState,
    setToggleReactivate,
    setInactiveAccount,
    handleReactivationSuccess as reactivationSuccessAction,
    restoreSameDeviceSession,
} from '../features/bankid/bankid.slice';
import {
    selectBankDetails,
    selectQRCodeData,
    selectIsInitializing,
    selectIsAuthenticating,
    selectIsQRModalOpen,
    selectQRState,
    selectIsAuthSuccessful,
    selectUserData,
    selectError,
    selectSessionTimeout,
    selectNeedsReload,
    selectIsReady,
    selectCanUseSameDevice,
    selectCanUseQRCode,
    selectToggleReactivate,
    selectInactiveAccount,
} from '../features/bankid/bankid.selectors';
import { getItem, setItem, removeItem } from '../helper/localstorage.helper';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

export interface UseBankIDOptions {
    mode: 'login' | 'signup';
    accountType?: 'freelancer' | 'business';
    onSuccess?: (userData: any) => void;
    onError?: (error: string) => void;
    autoInitialize?: boolean;
    collectMode?: 'default' | 'verification';
    skipSameDevicePolling?: boolean;
}

export const useBankID = (options: UseBankIDOptions) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const language = getItem('i18nextLng');
    const successHandledRef = useRef(false);
    const bankDetails = useSelector(selectBankDetails);
    const qrCodeData = useSelector(selectQRCodeData);
    const isInitializing = useSelector(selectIsInitializing);
    const isAuthenticating = useSelector(selectIsAuthenticating);
    const isQRModalOpen = useSelector(selectIsQRModalOpen);
    const qrState = useSelector(selectQRState);
    const isAuthSuccessful = useSelector(selectIsAuthSuccessful);
    const userData = useSelector(selectUserData);
    const error = useSelector(selectError);
    const sessionTimeout = useSelector(selectSessionTimeout);
    const needsReload = useSelector(selectNeedsReload);
    const isReady = useSelector(selectIsReady);
    const canUseSameDevice = useSelector(selectCanUseSameDevice);
    const canUseQRCode = useSelector(selectCanUseQRCode);
    const toggleReactivate = useSelector(selectToggleReactivate);
    const inactiveAccount = useSelector(selectInactiveAccount);

    // Close QR modal when reactivate modal should appear
    useEffect(() => {
        if (toggleReactivate) {
            dispatch(setQRModalOpen(false));
        }
    }, [toggleReactivate, dispatch]);

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();


    useEffect(() => {
        if (options.autoInitialize !== false) {
            const pendingSessionId = getItem('bankidSameDeviceSessionId');
            // When skipSameDevicePolling=true (signin/signup inline), auth is handled
            // by the BankID redirect URL on the backend — no frontend polling needed.
            // Clear any stale same-device session so it doesn't trigger spurious polling
            // on every page load, then proceed with normal GET /auth initialisation.
            if (options.skipSameDevicePolling && pendingSessionId) {
                removeItem('bankidSameDeviceSessionId');
            } else if (pendingSessionId && options.collectMode !== 'verification') {
                // On mobile, same-device BankID may navigate away and back (page reload),
                // wiping Redux. If a saved session ID exists, restore qrCodeData.id and
                // let the existing polling mechanism resume — skip normal init entirely.
                dispatch(restoreSameDeviceSession(pendingSessionId));
                return;
            }
            if (options.collectMode === 'verification') {
                dispatch(initializeBankIdVerificationDetails() as any);
            } else {
                dispatch(initializeBankIDDetails() as any);
            }
        }
    }, [dispatch, options.autoInitialize, options.collectMode, options.skipSameDevicePolling]);

    useEffect(() => {
        // Stop on any terminal state or while a request is already in-flight
        if (!qrCodeData?.id || isAuthSuccessful || sessionTimeout || needsReload || !!error || isAuthenticating) return;
        // For inline pages (skipSameDevicePolling=true) where same-device auth is
        // handled by the backend redirect, only poll while the QR view is actually
        // visible. This prevents the API from being called after the user clicks
        // back or after a successful auth clears the modal.
        if (options.skipSameDevicePolling && !isQRModalOpen) return;

        const lang = searchParams.get('lang') || language || 'en-us';
        const signupEmail = options.mode === 'signup' ? (getItem('SignUpemail') || undefined) : undefined;
        const tduid =
            options.mode === "signup"
                ? getItem("tduid") || undefined
                : undefined;
        const thunk = options.collectMode === 'verification'
            ? checkBankIdVerificationAuthStatus({ bankIdSession: qrCodeData.id, mode: options.mode, locale: lang })
            : checkBankIDAuthStatus({ bankIdSession: qrCodeData.id, mode: options.mode, email: signupEmail , tduid });

        // 2-second interval between polls; first call also uses 2s so the QR code is fully rendered first
        const timerId = setTimeout(() => {
            dispatch(thunk as any);
        }, 2000);

        return () => clearTimeout(timerId);
    }, [qrCodeData?.id, isAuthSuccessful, sessionTimeout, needsReload, error, isAuthenticating, isQRModalOpen, options.skipSameDevicePolling]);

    useEffect(() => {
        if (isAuthSuccessful && userData && options?.onSuccess && !successHandledRef.current) {
            successHandledRef.current = true;

            if (userData.isLoginWithBankId) {
                document.cookie = 'logged=1; path=/';
                toast.success(t('BANKID_LOGIN_SUCCESS') as string);
            }

            options.onSuccess(userData);
            dispatch(setQRModalOpen(false));

            const lang = searchParams.get("lang") || "en-us";
            if (!userData?.token) return;
            // @ts-ignore
            const preferredVersion = userData?.userData?.preferredPlatformVersion;
            setItem(
                'currentappversion',
                preferredVersion === 1 ? 'v1' : preferredVersion === 2 ? 'v2' : 'v2'
            );

            navigate(
                preferredVersion === 2
                    ? `/v2/dashboard?lang=${lang}`
                    : lang === 'en-us'
                        ? `/dashboard/activities?lang=${lang}`
                        : `/instrumentbrada/aktiviteter?lang=${lang}`
            );
            // Clear isAuthSuccessful from Redux AFTER navigating so the stale
            // `true` value doesn't trigger this effect again when useBankID is
            // mounted on another page (e.g. create-invoice) where a new local
            // successHandledRef starts as false.
            dispatch(resetBankIDFlow());
            return
        }
    }, [isAuthSuccessful, userData, options.onSuccess, dispatch, navigate, searchParams, t]);

    useEffect(() => {
        if (!isAuthSuccessful) {
            successHandledRef.current = false;
        }
    }, [isAuthSuccessful]);

    useEffect(() => {
        if (error && options.onError) {
            options.onError(error);
        }
    }, [error, options.onError]);

    const initializeQRFlow = useCallback(async (email?: string) => {
        dispatch(setQRModalOpen(true));
        if (options.collectMode === 'verification') {
            dispatch(initializeBankIdVerificationFlow() as any);
        } else {
            dispatch(initializeBankIDFlow(email ? { email } : undefined) as any);
        }
    }, [dispatch, options.collectMode]);

    const authenticateSameDevice = useCallback(async (email?: string , tduid?: string) => {
        // When autoInitialize=false, bankDetails are not pre-fetched on mount.
        // Fetch them now (GET /auth) before opening the BankID app so that
        // authenticateWithBankIDSameDevice can read bankDetails.id and autoStartToken.
        if (options.autoInitialize === false && !bankDetails?.id) {
            await dispatch(initializeBankIDDetails() as any);
        }
        await dispatch(authenticateWithBankIDSameDevice({
            mode: options.mode,
            accountType: options.accountType,
            language: language || 'sv-se',
            email,
            tduid
        }) as any);
    }, [dispatch, options.mode, options.accountType, options.autoInitialize, bankDetails, language]);

    const handleQRReload = useCallback(() => {
        if (!needsReload) return;
        dispatch(clearQRData());
        if (options.collectMode === 'verification') {
            dispatch(initializeBankIdVerificationFlow() as any);
        } else {
            const savedEmail = getItem('SignUpemail') || undefined;
            dispatch(initializeBankIDFlow(savedEmail ? { email: savedEmail } : undefined) as any);
        }
    }, [dispatch, needsReload, options.collectMode]);

    const closeQRModal = useCallback(() => {
        dispatch(resetBankIDFlow());
    }, [dispatch]);

    const resetState = useCallback(() => {
        dispatch(resetBankIDState());
    }, [dispatch]);

    const getTitle = useCallback(() => {
        return options.mode === 'login'
            ? t('Login with Bank ID')
            : t('Sign up with Bank ID');
    }, [options.mode, t]);

    const handleReactivationClose = useCallback(() => {
        console.log('useBankID: handleReactivationClose called');
        dispatch(setToggleReactivate(false));
        dispatch(setInactiveAccount(false));
    }, [dispatch]);

    const handleReactivationSuccess = useCallback(() => {
        console.log('useBankID: handleReactivationSuccess called');
        dispatch(reactivationSuccessAction());

        if (options.autoInitialize !== false) {
            if (options.collectMode === 'verification') {
                dispatch(initializeBankIdVerificationDetails() as any);
            } else {
                dispatch(initializeBankIDDetails() as any);
            }
        }
    }, [dispatch, options.autoInitialize, options.collectMode]);

    return {
        bankDetails,
        qrCodeData,
        isInitializing,
        isAuthenticating,
        isQRModalOpen,
        qrState,
        isAuthSuccessful,
        userData,
        error,
        sessionTimeout,
        needsReload,
        isReady,
        canUseSameDevice,
        canUseQRCode,
        toggleReactivate,
        inactiveAccount,
        initializeQRFlow,
        authenticateSameDevice,
        handleQRReload,
        closeQRModal,
        resetState,
        getTitle,
        language,
        handleReactivationClose,
        handleReactivationSuccess,
    };
};
